/** GET /whatsapp/broadcasts/{id}/gallery-funnel (dashboard-api
 *  broadcast_gallery_funnel.py): who came to my.echoflicks.com after a
 *  broadcast and what they did there. */

export type GalleryFunnelStageKey =
  | "link_opened"
  | "code_requested"
  | "signed_in"
  | "viewed_gallery"
  | "played"
  | "watched_to_end"
  | "create_clicked";

export type GalleryFunnelView = Record<GalleryFunnelStageKey, number> & {
  people: number;
  visits: number;
  plays: number;
  watch_minutes: number;
};

export type GalleryFunnelPerson = {
  contact_id: string;
  sent_at: string;
  tracked: boolean;
  stages: GalleryFunnelStageKey[];
  visits: number;
  plays: number;
  watch_minutes: number;
  first_activity_at: string;
  last_activity_at: string;
};

export type GalleryFunnel = {
  broadcast_id: string;
  window_days: number;
  computed_at: string;
  recipients_sent: number;
  stages: { key: GalleryFunnelStageKey; label: string }[];
  tracked: GalleryFunnelView;
  after_send: GalleryFunnelView;
  people: GalleryFunnelPerson[];
  notes?: Record<string, string>;
};

export const GALLERY_FUNNEL_WINDOWS = [1, 3, 7, 14, 30] as const;

export function galleryFunnelPath(
  broadcastId: string | number,
  windowDays: number,
  refresh = false,
) {
  const query = new URLSearchParams({ window_days: String(windowDays) });
  if (refresh) query.set("refresh", "1");
  return `/whatsapp/broadcasts/${encodeURIComponent(String(broadcastId))}/gallery-funnel?${query}`;
}

export type GalleryFunnelRow = {
  key: GalleryFunnelStageKey;
  label: string;
  value: number;
  /** Share of the recipients the broadcast was sent to, 0..100. */
  percent: number;
};

/** One row per stage, in the API's order, as a share of recipients sent. */
export function galleryFunnelRows(
  funnel: Pick<GalleryFunnel, "stages" | "recipients_sent">,
  view: GalleryFunnelView,
): GalleryFunnelRow[] {
  return funnel.stages.map(({ key, label }) => {
    const value = Number(view[key] ?? 0);
    const percent =
      funnel.recipients_sent > 0 ? (value / funnel.recipients_sent) * 100 : 0;
    return { key, label, value, percent };
  });
}

/** The furthest stage a person reached, for the people table. */
export function furthestStageLabel(
  funnel: Pick<GalleryFunnel, "stages">,
  person: Pick<GalleryFunnelPerson, "stages">,
): string {
  const order = funnel.stages.map((stage) => stage.key);
  const reached = person.stages
    .map((key) => order.indexOf(key))
    .filter((index) => index >= 0);
  if (!reached.length) return "Visited";
  return funnel.stages[Math.max(...reached)].label;
}
